import { forwardRef, Fragment, HTMLAttributes, useRef, useState } from 'react'
import { TransitionStatus } from 'react-transition-group'
import { useIsomorphicLayoutEffect } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Lenis from 'lenis'
import lowerCase from 'lodash/lowerCase'
import Link from 'next/link'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import LanguageList from '@/components/layout/LanguageList'
import Spacer from '@/components/sections/Spacer'
import { useScrollbar } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useLayoutStore } from '@/store/layout'

import { cn } from '@/lib/utils'
import { WPMenu } from '@/types'

export type MenuItemProps = HTMLAttributes<HTMLElement> & {
  menu?: WPMenu['menu']
  variant?: 'menu-sans' | 'menu-serif' | 'icon'
  dataName?: HTMLAttributes<HTMLAnchorElement>
}

const MenuItem = ({ menu, className, variant, ...props }: MenuItemProps) => {
  return (
    <nav
      className={cn(
        'flex',
        {
          'flex-row gap-[1.6rem]': variant === 'icon',
          'flex-col gap-[0.8rem]': variant !== 'icon'
        },
        className
      )}
      {...props}
    >
      {menu?.map(({ title, url, target }, index) => (
        <Fragment key={`menu-${index}`}>
          <div
            className={cn('overflow-y-hidden', {
              flex: variant !== 'icon'
            })}
          >
            <Link
              href={url}
              target={target}
              className={cn({
                'group block h-[1.4rem] max-w-[2rem] items-center justify-center':
                  variant === 'icon',
                'w-full text-center transition-colors duration-300 ease-out-cubic hover:text-dark-300':
                  variant !== 'icon'
              })}
              data-motion-link-menu
              scroll={false}
            >
              {variant === 'icon' ? (
                <SVGIcon
                  symbol={`${lowerCase(title)}-icon`}
                  className={cn([
                    'h-full w-auto shrink-0 fill-dark-500',
                    'transition-colors duration-300 ease-out-cubic group-hover:fill-dark-900'
                  ])}
                />
              ) : (
                <Typography variant={variant}>{title}</Typography>
              )}
            </Link>
          </div>
        </Fragment>
      ))}
    </nav>
  )
}

export type MenuProps = HTMLAttributes<HTMLDivElement> & {
  state: TransitionStatus
  menuOpen: boolean
}

const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ state, menuOpen, ...props }, ref) => {
    const { menu } = useStore()
    const [lenis, setLenis] = useState<Lenis>()
    const [track, thumb, needsScrollbar] = useScrollbar(lenis)
    const wrapper = useRef<HTMLDivElement>(null!)
    const content = useRef<HTMLDivElement>(null!)
    const setLayoutState = useLayoutStore((state) => state.setLayoutState)

    useIsomorphicLayoutEffect(() => {
      const lenis = new Lenis({
        smoothWheel: true,
        wrapper: wrapper.current,
        content: content.current
      })

      const tick = (time: number) => {
        lenis.raf(time * 1000)
      }

      gsap.ticker.add(tick)

      setLenis(lenis)

      return () => {
        gsap.ticker.remove(tick)
        lenis.destroy()
        setLenis(undefined)
      }
    }, [])

    useGSAP(
      () => {
        if (!menuOpen) return
        const timeline = gsap.timeline({ delay: 0.6 })
        timeline
          .fromTo(
            ['[data-motion-link-menu]'],
            {
              y: '-100%'
            },
            {
              y: '0%',
              duration: 0.8,
              ease: 'power4.out',
              stagger: 0.05
            }
          )
          .fromTo(
            ['[data-motion-language-menu]', '[data-motion-image-menu]'],
            {
              autoAlpha: 0
            },
            {
              autoAlpha: 1,
              duration: 1.2,
              ease: 'power1.out'
            },
            '<0.2'
          )
        return () => {
          timeline.reverse()
        }
      },
      { scope: wrapper, dependencies: [menuOpen] }
    )

    return (
      <>
        <div
          ref={ref}
          className={cn(
            'fixed left-0 top-0 z-10 h-auto w-full',
            'grid bg-white text-dark-500',
            'transition-all duration-1000 ease-in-out-cubic',
            {
              'grid-rows-[0fr] duration-500':
                state === 'entering' || state === 'exiting'
            },
            { 'grid-rows-[1fr]': state === 'entered' }
          )}
          data-motion-background
          {...props}
        >
          <div ref={wrapper} className="overflow-hidden">
            <div
              ref={content}
              className="flex min-h-screen pb-[6rem] pt-[6rem]"
            >
              <Container>
                <Grid className="h-full">
                  <div
                    className={cn([
                      'col-span-4 md:col-span-6 lg:col-span-12',
                      'flex flex-col items-center justify-center'
                    ])}
                  >
                    <MenuItem
                      menu={menu?.main_menu_01?.menu}
                      variant="menu-sans"
                    />
                    <Spacer variant="xs" theme="transparent" />
                    <MenuItem
                      menu={menu?.main_menu_02?.menu}
                      variant="menu-serif"
                    />
                    <Spacer variant="md" theme="transparent" />

                    <MenuItem menu={menu?.social_menu?.menu} variant="icon" />
                    <Spacer variant="md" theme="transparent" />
                    <LanguageList data-motion-language-menu />
                  </div>
                </Grid>
              </Container>
            </div>
          </div>

          <div
            className={cn([
              'group absolute bottom-0 right-0 top-0 z-scrollbar w-[1.2rem] touch:hidden',
              {
                visible: needsScrollbar,
                invisible: !needsScrollbar
              }
            ])}
          >
            <div ref={track} className="relative h-full w-full">
              <div
                ref={thumb}
                className={cn([
                  'absolute left-0 top-0 w-full',
                  'before:absolute before:bottom-[.2rem] before:left-[.4rem] before:top-[.2rem] before:w-[.6rem] before:rounded-full before:bg-white'
                ])}
              />
            </div>
          </div>
        </div>
        <button
          className={cn([
            'absolute left-0 top-0 z-0 h-svh w-full bg-black/70',
            {
              'opacity-0': state === 'entering',
              'opacity-100 transition-opacity duration-300 ease-in-out-cubic':
                state === 'entered',
              'opacity-0 transition-opacity duration-300 ease-in-out-cubic':
                state === 'exiting'
            }
          ])}
          onClick={() => setLayoutState({ menuOpen: false })}
        />
      </>
    )
  }
)

export default Menu
